import {
  Button,
  HookedForm,
  Loading,
  DateRangePicker,
  Tooltip,
  Pagination,
} from '@app/components';
import React from 'react';
import { useRatingsReviews } from './RatingsReviews.hooks';
import { PreviewReview } from '@app/components/PreviewReview/PreviewReview';
import { ReviewBox } from '@app/components/ReviewBox/ReviewBox';
import { BusinessReviews, ExternalReview } from '@app/models/reviews';
import NoReviewsIllustration from '@app/assets/illustrations/no-reviews.svg?react';
// import YelpIcon from '@app/assets/icons/integrations/yelp.svg?react';
import GoogleLogo from '@app/assets/icons/Google.svg?react';
import NoReviewImage from '@app/assets/images/no-review.png';
import { useNavigate } from 'react-router-dom';
import { useState, useRef } from 'react';
import { clsx } from 'clsx';
import SearchBoxImage from '@app/assets/images/search_box.svg?react';
import { usePermissions } from '@app/contexts/PermissionContext.tsx';


export const RatingsReviews = () => {
  const {
    filtersForm,
    requestData,
    handleReviewClick,
    activeReview,
    isLoading,
    activeTab,
    tabOptions,
    handleTabClick,
    page,
    // limit,
    handleLimitChange,
    handleNextPage,
    handlePreviousPage,
  } = useRatingsReviews();
  const {can} = usePermissions()

  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState(
    filtersForm.getValues('search') || ''
  );
  const [filteredReviews, setFilteredReviews] = useState<
    BusinessReviews[] | ExternalReview[] | null
  >(null);
  // const [activeTab, setActiveTab] = useState<'all' | 'new' | 'needs-response' | 'with-responses'>('all');
  const [platformsOpen, setPlatformsOpen] = useState(false);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const platformsRef = useRef<HTMLDivElement>(null);
  const [ratingsOpen, setRatingsOpen] = useState(false);
  const [selectedRatings, setSelectedRatings] = useState<string[]>([]);
  const [ratingsDropdownPosition, setRatingsDropdownPosition] = useState({
    top: 0,
    left: 0,
  });
  const ratingsRef = useRef<HTMLDivElement>(null);
  const [sortOpen, setSortOpen] = useState(false);

  // Sort options
  const sortOptions = [
    { value: 'newest', label: 'Newest reviews' },
    { value: 'oldest', label: 'Oldest reviews' },
    { value: 'highest', label: 'Highest rating' },
    { value: 'lowest', label: 'Lowest rating' },
    { value: 'shortest', label: 'Shortest reviews' },
    { value: 'longest', label: 'Longest reviews' },
  ];

  // Platform options with icons
  const platformOptions = [
    {
      value: 'google',
      label: 'Google',
      icon: <GoogleLogo />,
    },
    // {
    //   value: 'yelp',
    //   label: 'Yelp',
    //   icon: <YelpIcon />,
    // },
  ];

  // Handle platform toggle
  const handlePlatformToggle = (platformValue: string) => {
    const newSelected = selectedPlatforms.includes(platformValue)
      ? selectedPlatforms.filter((p) => p !== platformValue)
      : [...selectedPlatforms, platformValue];

    setSelectedPlatforms(newSelected);
    // If no platforms are selected, set to 'all' instead of empty array
    filtersForm.setValue('platforms', newSelected.length === 0 ? ['all'] : newSelected);
  };

  // Rating options with star icons
  const ratingOptions = [
    { value: '5', label: '5', icon: '⭐' },
    { value: '4', label: '4', icon: '⭐' },
    { value: '3', label: '3', icon: '⭐' },
    { value: '2', label: '2', icon: '⭐' },
    { value: '1', label: '1', icon: '⭐' },
  ];

  // Handle rating toggle
  const handleRatingToggle = (ratingValue: string) => {
    const newSelected = selectedRatings.includes(ratingValue)
      ? selectedRatings.filter((r) => r !== ratingValue)
      : [...selectedRatings, ratingValue];

    setSelectedRatings(newSelected);
    // If no ratings are selected, set to 'all' instead of empty array
    filtersForm.setValue('ratings', newSelected.length === 0 ? ['all'] : newSelected);
  };

  // Debounce search input
  React.useEffect(() => {
    const startDate = filtersForm.getValues('start');
    const endDate = filtersForm.getValues('end');
    
    // Only trigger search if both dates are selected
    if (startDate && endDate) {
      const handler = setTimeout(() => {
        filtersForm.setValue('search', searchValue);
      }, 400);
      return () => clearTimeout(handler);
    }
  }, [searchValue, filtersForm.watch('start'), filtersForm.watch('end')]);

  // Reset filter if search is cleared
  React.useEffect(() => {
    if (searchValue.trim() === '') {
      setFilteredReviews(null);
    }
  }, [searchValue]);

  // Watch for sort changes to trigger re-render
  const sortValue = filtersForm.watch('sort');
  React.useEffect(() => {
    // This effect will trigger re-render when sort value changes
  }, [sortValue]);

  // Close platforms dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.platforms-dropdown')) {
        setPlatformsOpen(false);
      }
      if (!target.closest('.ratings-dropdown')) {
        setRatingsOpen(false);
      }
    };

    if (platformsOpen || ratingsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [platformsOpen, ratingsOpen]);

  // Get tab-specific empty state message
  const getEmptyStateMessage = () => {
    switch (activeTab) {
      case 'new':
        return {
          title: 'No new reviews',
          description: 'This section displays reviews from the past 7 days.',
        };
      case 'needs-response':
        return {
          title: 'No reviews that require a response',
          description:
            'Reviews that are in need of a response will be displayed here.',
        };
      case 'with-responses':
        return {
          title: 'No reviews with responses',
          description:
            'Reviews that include a response from your business will be displayed here.',
        };
      default:
        return {
          title: 'No reviews found',
          description:
            'No reviews match your current filters. Consider adjusting them.',
        };
    }
  };

  // // Sort reviews based on selected sort option
  // const sortReviews = (reviews: BusinessReviews[]) => {
  //   const sortValue = filtersForm.getValues('sort') || 'newest';
  //
  //   // todo: refactor this to be sorted at backend
  //   switch (sortValue) {
  //     case 'newest':
  //       return [...reviews].sort(
  //         (a, b) =>
  //           new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  //       );
  //     case 'oldest':
  //       return [...reviews].sort(
  //         (a, b) =>
  //           new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  //       );
  //     case 'highest_rating':
  //       return [...reviews].sort((a, b) => (b.rating || 0) - (a.rating || 0));
  //     case 'lowest_rating':
  //       return [...reviews].sort((a, b) => (a.rating || 0) - (b.rating || 0));
  //     case 'shortest':
  //       return [...reviews].sort(
  //         (a, b) => (a.review?.length || 0) - (b.review?.length || 0)
  //       );
  //     case 'longest':
  //       return [...reviews].sort(
  //         (a, b) => (b.review?.length || 0) - (a.review?.length || 0)
  //       );
  //     default:
  //       return reviews;
  //   }
  // };

  // Get sorted and filtered reviews
  // const getSortedAndFilteredReviews = () => {
  //   const baseReviews = filteredReviews;
  //   return baseReviews;
  //   // return sortReviews(baseReviews);
  // };


  // Render star rating
  const renderStarRating = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    return (
      <div className="flex items-center gap-1">
        {[...Array(5)].map((_, index) => {
          if (index < fullStars) {
            return (
              <svg
                key={index}
                width="16"
                height="16"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9951 2.7143C12.2017 2.29575 12.7985 2.29575 13.0051 2.7143L15.6791 8.13242C15.7611 8.29863 15.9196 8.41383 16.1031 8.44048L22.0823 9.30932C22.5442 9.37643 22.7286 9.94406 22.3944 10.2698L18.0678 14.4873C17.9351 14.6166 17.8745 14.803 17.9058 14.9857L18.9272 20.9408C19.0061 21.4008 18.5233 21.7516 18.1101 21.5345L12.7621 18.7228C12.5981 18.6366 12.4021 18.6366 12.238 18.7228L6.89002 21.5345C6.47689 21.7516 5.99404 21.4008 6.07294 20.9408L7.09432 14.9857C7.12565 14.803 7.06509 14.6166 6.93236 14.4873L2.60573 10.2698C2.2715 9.94406 2.45594 9.37643 2.91783 9.30932L8.89708 8.44048C9.0805 8.41383 9.23906 8.29863 9.32109 8.13242L11.9951 2.7143Z"
                  fill="#FE621D"
                />
              </svg>
            );
          } else if (index === fullStars && hasHalfStar) {
            return (
              <svg
                key={index}
                width="16"
                height="16"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <defs>
                  <linearGradient id="halfStar">
                    <stop offset="50%" stopColor="#FE621D" />
                    <stop offset="50%" stopColor="#E5E7EB" />
                  </linearGradient>
                </defs>
                <path
                  d="M11.9951 2.7143C12.2017 2.29575 12.7985 2.29575 13.0051 2.7143L15.6791 8.13242C15.7611 8.29863 15.9196 8.41383 16.1031 8.44048L22.0823 9.30932C22.5442 9.37643 22.7286 9.94406 22.3944 10.2698L18.0678 14.4873C17.9351 14.6166 17.8745 14.803 17.9058 14.9857L18.9272 20.9408C19.0061 21.4008 18.5233 21.7516 18.1101 21.5345L12.7621 18.7228C12.5981 18.6366 12.4021 18.6366 12.238 18.7228L6.89002 21.5345C6.47689 21.7516 5.99404 21.4008 6.07294 20.9408L7.09432 14.9857C7.12565 14.803 7.06509 14.6166 6.93236 14.4873L2.60573 10.2698C2.2715 9.94406 2.45594 9.37643 2.91783 9.30932L8.89708 8.44048C9.0805 8.41383 9.23906 8.29863 9.32109 8.13242L11.9951 2.7143Z"
                  fill="url(#halfStar)"
                />
              </svg>
            );
          } else {
            return (
              <svg
                key={index}
                width="16"
                height="16"
                viewBox="0 0 25 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.9951 2.7143C12.2017 2.29575 12.7985 2.29575 13.0051 2.7143L15.6791 8.13242C15.7611 8.29863 15.9196 8.41383 16.1031 8.44048L22.0823 9.30932C22.5442 9.37643 22.7286 9.94406 22.3944 10.2698L18.0678 14.4873C17.9351 14.6166 17.8745 14.803 17.9058 14.9857L18.9272 20.9408C19.0061 21.4008 18.5233 21.7516 18.1101 21.5345L12.7621 18.7228C12.5981 18.6366 12.4021 18.6366 12.238 18.7228L6.89002 21.5345C6.47689 21.7516 5.99404 21.4008 6.07294 20.9408L7.09432 14.9857C7.12565 14.803 7.06509 14.6166 6.93236 14.4873L2.60573 10.2698C2.2715 9.94406 2.45594 9.37643 2.91783 9.30932L8.89708 8.44048C9.0805 8.41383 9.23906 8.29863 9.32109 8.13242L11.9951 2.7143Z"
                  fill="#E5E7EB"
                />
              </svg>
            );
          }
        })}
      </div>
    );
  };

  // Handler for search icon click
  const handleSearchClick = () => {

    filtersForm.setValue('search', searchValue.trim().toLowerCase());

    // const search = searchValue.trim().toLowerCase();
    // if (!search) {
    //   setFilteredReviews(null);
    //   return;
    // }
    // const allReviews = requestData?.data?.reviews || [];
    // setFilteredReviews(
    //   allReviews.filter(
    //     (review) =>
    //       (review.review && review.review.toLowerCase().includes(search)) ||
    //       (review.reply && review.reply.toLowerCase().includes(search)) ||
    //       review.firstName?.toLowerCase().includes(search) ||
    //       review.lastName?.toLowerCase().includes(search) ||
    //       (review.firstName &&
    //         review.lastName &&
    //         `${review.firstName} ${review.lastName}`
    //           .toLowerCase()
    //           .includes(search))
    //   )
    // );
  };

  return (
    <div>
      <div className="bg-white border-b border-b-base-ghostWhite ">
        <div className="container mx-auto p-4 flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="font-bold text-xl md:text-[28px]">Reviews</h2>
            {!isLoading && requestData?.data?.reviews && requestData?.data?.count > 0 && (
              <div className="bg-gray-100 rounded-lg px-3 py-2 flex items-center gap-2">
                <span className="text-gray-900 font-medium">{requestData?.data?.averageRating?.toFixed(2)}</span>
                {renderStarRating(requestData?.data?.averageRating)}
              </div>
            )}
          </div>
        </div>

        {/* Tab Filters */}
        {(requestData?.data?.count > 0 ||
          (requestData?.data?.count === 0 &&
            (selectedPlatforms.length > 0 || selectedRatings.length > 0 || searchValue.trim() !== '' || filtersForm.getValues('start') !== '' || filtersForm.getValues('end') !== ''))) && (
          <>
            <div className="container mx-auto px-4 py-4">
              <div className="flex space-x-1">
                {tabOptions.map((tabOption) => {
                  const button = (
                    <button
                      onClick={() => tabOption.handle()}
                      className={clsx(
                        'px-4 py-2 text-sm font-medium rounded-t-lg transition-colors',
                        tabOption.active ? 'bg-white text-black border-b-2 border-black' : 'text-gray-500 hover:text-gray-700'
                      )}
                    >
                      {tabOption.label}
                      <span className="ml-2 text-xs bg-gray-100 px-2 py-0.5 rounded-md">{tabOption.count}</span>
                    </button>
                  );

                  return tabOption.tooltip ? (
                    <Tooltip content={tabOption.tooltip} key={tabOption.value}>
                      {button}
                    </Tooltip>
                  ) : (
                    <React.Fragment key={tabOption.value}>{button}</React.Fragment>
                  );
                })}
              </div>
            </div>

            {/* Full-width separator line for tab filters */}
            <hr className="border-0 border-b border-gray-200 w-full -mt-4" />
          </>
        )}

        {(requestData?.data?.count > 0 ||
          (requestData?.data?.count === 0 &&
            (selectedPlatforms.length > 0 || selectedRatings.length > 0 || searchValue.trim() !== '' || filtersForm.getValues('start') !== '' || filtersForm.getValues('end') !== ''))) && (
          <div className="container mx-auto p-4">
            <div className="flex items-center w-full overflow-x-auto">
              <HookedForm formHook={filtersForm} className="flex items-center flex-1 min-w-0 overflow-visible">
                {/* Date Range Picker */}
                <DateRangePicker
                  startName="start"
                  endName="end"
                  startPlaceholder="Start date"
                  endPlaceholder="End date"
                  className={`bg-gray-100 border-0 rounded-lg px-4 py-2 text-gray-600 placeholder:text-gray-500 focus:bg-gray-200 focus:outline-none ${
                    filtersForm.getValues('start') || filtersForm.getValues('end') ? 'ring-2 ring-[#52B6FE]' : ''
                  }`}
                  containerClass="flex-shrink-0 mr-3"
                />

                {/* Grouped Filters Container */}
                <div className="flex items-center gap-3 mr-auto m-2">
                  {/* All Platforms Dropdown */}
                  <div className="relative platforms-dropdown z-10">
                    <div className="relative">
                      <div
                        ref={platformsRef}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newState = !platformsOpen;

                          if (newState && platformsRef.current) {
                            const rect = platformsRef.current.getBoundingClientRect();
                            setDropdownPosition({
                              top: rect.bottom + 4,
                              left: rect.left,
                            });
                          }

                          // Close other dropdowns when opening platforms dropdown
                          if (newState) {
                            setRatingsOpen(false);
                            setSortOpen(false);
                          }

                          setPlatformsOpen(newState);
                        }}
                        className={`bg-gray-100 border-0 rounded-lg px-3 py-2.5 pr-8 text-gray-900 focus:bg-gray-200 focus:outline-none text-sm cursor-pointer min-w-[160px] max-w-[300px] flex items-center justify-between ${
                          platformsOpen || selectedPlatforms.length > 0 ? 'ring-2 ring-[#52B6FE]' : ''
                        }`}
                      >
                        <span className="text-gray-500 flex-1 truncate">
                          {selectedPlatforms.length > 0 ? selectedPlatforms.map((platform) => platformOptions.find((p) => p.value === platform)?.label).join(', ') : 'All platforms'}
                        </span>
                        <div className="flex items-center gap-1">
                          {platformsOpen ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#8B919F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={`transition-transform duration-200`}>
                              <path d="M4 6L8 10L12 6" stroke="#8B919F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </div>

                      {platformsOpen && (
                        <div
                          className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-[99999]"
                          style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                          }}
                        >
                          <div>
                            {platformOptions.map((platform, index) => (
                              <div key={platform.value}>
                                <div
                                  onClick={() => handlePlatformToggle(platform.value)}
                                  className={`flex items-center gap-3 px-2 py-2 cursor-pointer ${selectedPlatforms.includes(platform.value) ? 'bg-[#F8FCFF]' : ''}`}
                                >
                                  <div className="w-6 h-6 flex items-center justify-center">{platform.icon}</div>
                                  <span className="flex-1 text-sm text-gray-900">{platform.label}</span>
                                  {selectedPlatforms.includes(platform.value) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handlePlatformToggle(platform.value);
                                      }}
                                      className="text-gray-400 hover:text-gray-600 mr-2"
                                    ></button>
                                  )}
                                  <div
                                    className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                      selectedPlatforms.includes(platform.value) ? 'bg-[#52B6FE] border-[#52B6FE]' : 'border-gray-300'
                                    }`}
                                  >
                                    {selectedPlatforms.includes(platform.value) && (
                                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                {index < platformOptions.length - 1 && <div className="border-b border-gray-200"></div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* All Ratings Dropdown */}
                  <div className="relative ratings-dropdown z-10">
                    <div className="relative">
                      <div
                        ref={ratingsRef}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newState = !ratingsOpen;

                          if (newState && ratingsRef.current) {
                            const rect = ratingsRef.current.getBoundingClientRect();
                            setRatingsDropdownPosition({
                              top: rect.bottom + 4,
                              left: rect.left,
                            });
                          }

                          // Close other dropdowns when opening ratings dropdown
                          if (newState) {
                            setPlatformsOpen(false);
                            setSortOpen(false);
                          }

                          setRatingsOpen(newState);
                        }}
                        className={`bg-gray-100 border-0 rounded-lg px-3 py-2.5 pr-8 text-gray-900 focus:bg-gray-200 focus:outline-none text-sm cursor-pointer w-40 flex items-center justify-between ${
                          ratingsOpen || selectedRatings.length > 0 ? 'ring-2 ring-[#52B6FE]' : ''
                        }`}
                      >
                        <span className="text-gray-500">{selectedRatings.length > 0 ? selectedRatings.join(', ') : 'All ratings'}</span>
                        <div className="flex items-center gap-1">
                          {ratingsOpen ? (
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5" stroke="#8B919F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          ) : (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className={`transition-transform duration-200 ${ratingsOpen ? 'rotate-180' : ''}`}
                            >
                              <path d="M4 6L8 10L12 6" stroke="#8B919F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </div>

                      {ratingsOpen && (
                        <div
                          className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-[99999]"
                          style={{
                            top: `${ratingsDropdownPosition.top}px`,
                            left: `${ratingsDropdownPosition.left}px`,
                          }}
                        >
                          <div>
                            {ratingOptions.map((rating, index) => (
                              <div key={rating.value}>
                                <div
                                  onClick={() => handleRatingToggle(rating.value)}
                                  className={`flex items-center gap-3 px-2 py-2 cursor-pointer ${selectedRatings.includes(rating.value) ? 'bg-[#F8FCFF]' : ''}`}
                                >
                                  <div className="w-6 h-6 flex items-center justify-center">
                                    <svg width="20" height="20" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        d="M11.9951 2.7143C12.2017 2.29575 12.7985 2.29575 13.0051 2.7143L15.6791 8.13242C15.7611 8.29863 15.9196 8.41383 16.1031 8.44048L22.0823 9.30932C22.5442 9.37643 22.7286 9.94406 22.3944 10.2698L18.0678 14.4873C17.9351 14.6166 17.8745 14.803 17.9058 14.9857L18.9272 20.9408C19.0061 21.4008 18.5233 21.7516 18.1101 21.5345L12.7621 18.7228C12.5981 18.6366 12.4021 18.6366 12.238 18.7228L6.89002 21.5345C6.47689 21.7516 5.99404 21.4008 6.07294 20.9408L7.09432 14.9857C7.12565 14.803 7.06509 14.6166 6.93236 14.4873L2.60573 10.2698C2.2715 9.94406 2.45594 9.37643 2.91783 9.30932L8.89708 8.44048C9.0805 8.41383 9.23906 8.29863 9.32109 8.13242L11.9951 2.7143Z"
                                        fill="#FE621D"
                                      />
                                    </svg>
                                  </div>
                                  <span className="flex-1 text-sm text-gray-900">{rating.label}</span>
                                  {selectedRatings.includes(rating.value) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleRatingToggle(rating.value);
                                      }}
                                      className="text-gray-400 hover:text-gray-600 mr-2"
                                    ></button>
                                  )}
                                  <div
                                    className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                      selectedRatings.includes(rating.value) ? 'bg-[#52B6FE] border-[#52B6FE]' : 'border-gray-300'
                                    }`}
                                  >
                                    {selectedRatings.includes(rating.value) && (
                                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                {index < ratingOptions.length - 1 && <div className="border-b border-gray-200"></div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reset Filter Button */}
                  {(() => {
                    const hasActiveFilters =
                      selectedPlatforms.length > 0 ||
                      selectedRatings.length > 0 ||
                      searchValue.trim() !== '' ||
                      filtersForm.getValues('sort') !== 'newest-reviews' ||
                      filtersForm.getValues('start') !== '' ||
                      filtersForm.getValues('end') !== '';

                    return hasActiveFilters ? (
                      <button
                        type="button"
                        onClick={() => {
                          filtersForm.reset();
                          setFilteredReviews(null);
                          setSearchValue('');
                          handleTabClick('all');
                          setSelectedPlatforms([]);
                          setPlatformsOpen(false);
                          filtersForm.setValue('platforms', ['all']);
                          setSelectedRatings([]);
                          setRatingsOpen(false);
                        }}
                        className="flex items-center gap-1 text-red-500 hover:text-red-600 transition-colors cursor-pointer"
                      >
                        <span className="text-sm">Reset</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    ) : null;
                  })()}
                </div>

                {/* Sort Dropdown - positioned on the right */}
                <div className="ml-auto flex-shrink-0">
                  <div className="relative">
                    <div className="relative">
                      <div
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newState = !sortOpen;

                          if (newState) {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setDropdownPosition({
                              top: rect.bottom + 4,
                              left: rect.left,
                            });
                          }

                          // Close other dropdowns when opening sort dropdown
                          if (newState) {
                            setPlatformsOpen(false);
                            setRatingsOpen(false);
                          }

                          setSortOpen(newState);
                        }}
                        className="bg-[#F8F9FB] rounded-lg px-4 py-2 pr-10 text-gray-900 focus:border-[#3B82F6] focus:outline-none focus:ring-2 focus:ring-[#3B82F6] focus:ring-opacity-20 text-sm cursor-pointer w-full flex items-center justify-between"
                      >
                        <span>{sortOptions.find((opt) => opt.value === filtersForm.getValues('sort'))?.label || 'Newest reviews'}</span>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={`transition-transform duration-200 ${sortOpen ? 'rotate-180' : ''}`}>
                          <path d="M4 6L8 10L12 6" stroke="#8B919F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>

                      {sortOpen && (
                        <div
                          className="fixed w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-[99999]"
                          style={{
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                          }}
                        >
                          {sortOptions.map((option) => (
                            <div
                              key={option.value}
                              onClick={() => {
                                filtersForm.setValue('sort', option.value);
                                setSortOpen(false);
                              }}
                              className={`flex items-center p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                filtersForm.getValues('sort') === option.value ? 'bg-[#F8FCFF]' : 'hover:bg-[#F8FCFF]'
                              }`}
                            >
                              <span className="text-sm text-gray-900">{option.label}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M13.4415 6.62732H22M13.4415 11.4421H19.5547M13.4415 16.2569H17.1094M5.80564 6V18M5.80564 18L2 14.3317M5.80564 18L9.7566 14.3317"
                          stroke="#8B919F"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </HookedForm>
            </div>
          </div>
        )}
        {/* Reviews counter and search row */}
        {(requestData?.data?.count > 0 ||
          (requestData?.data?.count === 0 &&
            (selectedPlatforms.length > 0 || selectedRatings.length > 0 || searchValue.trim() !== '' || filtersForm.getValues('start') !== '' || filtersForm.getValues('end') !== ''))) && (
          <div className="container mx-auto flex justify-between items-center py-4 sm:py-6 px-4">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold">{requestData?.data?.filtered ?? 0} reviews</h3>
            <Tooltip content="Search review">
              <div className="relative w-full max-w-sm sm:max-w-xs md:max-w-md">
                <input
                  type="text"
                  className="w-full border-0 border-b border-base-ghostGray text-base sm:text-lg py-2 pr-10 focus:outline-none focus:ring-0 focus:border-primary-100 bg-transparent placeholder:text-base-coolGray"
                  placeholder="Search"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearchClick()}
                  style={{ fontWeight: 400 }}
                />
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-base-coolGray" onClick={handleSearchClick} aria-label="Search">
                  <svg width="24" height="24" className="sm:w-7 sm:h-7" fill="none" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
                    <path stroke="currentColor" strokeWidth="2" strokeLinecap="round" d="M21 21l-3.5-3.5" />
                  </svg>
                </button>
              </div>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Main loading state */}
      {isLoading && (
        <div className="flex items-center justify-center min-h-[400px]">
          <Loading />
        </div>
      )}

      {requestData?.data?.count > 0 && !isLoading && !(filteredReviews && filteredReviews.length === 0) && (
        <>
          {requestData?.data?.reviews && requestData?.data?.reviews?.length > 0 ? (
            <div id="test" className="grid grid-cols-1 lg:grid-cols-6 gap-0 lg:gap-4 min-h-screen">
              <div className="col-span-1 lg:col-span-2 bg-white h-full">
                <ul className="divide-y divide-gray-100">
                  {requestData?.data?.reviews?.map((review: ExternalReview) => (
                    <li key={review.id} onClick={() => handleReviewClick(review.id)} className="cursor-pointer hover:bg-gray-50 transition-colors">
                      <PreviewReview review={review} active={activeReview === review.id} />
                    </li>
                  ))}
                </ul>
              </div>
              <div className="col-span-1 lg:col-span-4 p-4">
                {activeReview ? (
                  (() => {
                    const selectedReview = requestData?.data?.reviews?.find((review: ExternalReview | BusinessReviews) => review.id === activeReview);
                    return selectedReview ? <ReviewBox review={selectedReview as ExternalReview} /> : null;
                  })()
                ) : (
                  <div className="hidden lg:flex items-center justify-center h-64">
                    <p className="text-base-coolGray text-center">Select a review from the list to view details</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center flex-col max-w-[280px] sm:max-w-[350px] mx-auto space-y-3 sm:space-y-4 h-full mt-16 sm:mt-20 px-4">
              <img src={NoReviewImage} alt="No reviews" className="w-32 h-32 object-contain" />
              <p className="font-bold text-lg text-gray-900 text-center">{getEmptyStateMessage().title}</p>
              <p className="text-sm text-gray-500 text-center leading-relaxed">{getEmptyStateMessage().description}</p>
            </div>
          )}
          <hr className="border-gray-200 mb-2" />
          {/*<pre style={{ border: 'solid 1px red', textAlign: 'left' }}>{JSON.stringify(requestData?.data, null, 4)}</pre>*/}
          {/*<pre style={{ border: 'solid 1px green', textAlign: 'left' }}>{JSON.stringify({ page, limit }, null, 4)}</pre>*/}
          {requestData?.data?.pages && (
            <Pagination handleLimitChange={handleLimitChange} handleNextPage={handleNextPage} handlePrevPage={handlePreviousPage} page={page} total={requestData?.data?.count} />
          )}
        </>
      )}

      {/* Show "No reviews found" when search returns no results */}
      {requestData?.data?.count > 0 && !isLoading && filteredReviews && filteredReviews.length === 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-0 lg:gap-4 min-h-screen">
          <div className="col-span-1 lg:col-span-2 bg-white h-full flex items-center justify-center">
            <div className="flex flex-col items-center space-y-4 px-4">
              <SearchBoxImage className="w-18 h-18 object-contain" aria-label="No reviews found" />
              <p className="font-bold text-lg text-gray-900 text-center">No reviews found</p>
              <p className="text-sm text-gray-500 text-center leading-relaxed">
                No reviews match your current
                <br />
                filters. Consider adjusting them.
              </p>
            </div>
          </div>
          <div className="col-span-1 lg:col-span-4">{/* Empty right panel */}</div>
        </div>
      )}

      {/* Show search empty state when no reviews exist but filters are applied */}
      {requestData?.data?.count === 0 &&
        !isLoading &&
        (selectedPlatforms.length > 0 || selectedRatings.length > 0 || searchValue.trim() !== '' || filtersForm.getValues('start') !== '' || filtersForm.getValues('end') !== '') && (
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-0 lg:gap-4 min-h-screen">
            <div className="col-span-1 lg:col-span-2 bg-white h-full flex items-center justify-center">
              <div className="flex flex-col items-center space-y-4 px-4">
                <SearchBoxImage className="w-18 h-18 object-contain" aria-label="No reviews found" />
                <p className="font-bold text-lg text-gray-900 text-center">No reviews found</p>
                <p className="text-sm text-gray-500 text-center leading-relaxed">
                  No reviews match your current
                  <br />
                  filters. Consider adjusting them.
                </p>
              </div>
            </div>
            <div className="col-span-1 lg:col-span-4">{/* Empty right panel */}</div>
          </div>
        )}

      {/* Show main empty state only when no reviews exist and no filters are applied */}
      {requestData?.data?.count === 0 &&
        !isLoading &&
        selectedPlatforms.length === 0 &&
        selectedRatings.length === 0 &&
        searchValue.trim() === '' &&
        filtersForm.getValues('start') === '' &&
        filtersForm.getValues('end') === '' && (
          <div className="flex items-center justify-center flex-col max-w-[280px] sm:max-w-[350px] mx-auto space-y-3 sm:space-y-4 h-full mt-16 sm:mt-20 px-4">
            <NoReviewsIllustration className="w-16 h-16 sm:w-auto sm:h-auto" />
            <p className="font-bold text-base sm:text-lg text-center">No reviews</p>
            <p className="text-sm sm:text-base text-base-coolGray text-center leading-relaxed">
              You currently don't have any reviews.
              <br />
              Start a campaign to send review requests
              <br /> across your platforms.
            </p>
            {can('createCampaigns') && (
              <Button type="button" onClick={() => navigate('/campaigns?schedule=true')}>
                Schedule a campaign
              </Button>
            )}
          </div>
        )}
    </div>
  );
};
